import { useCallback, useEffect, useRef, useState } from 'react';

import { loadPeaks } from '@/utils/audioPeaks';

/**
 * Every clip element alive on the page. Starting one pauses the rest, so
 * the representative sound and a timeline fragment never play over each
 * other.
 */
const liveClips = new Set<HTMLAudioElement>();

function pauseOthers(current: HTMLAudioElement) {
  liveClips.forEach((clip) => {
    if (clip !== current && !clip.paused) {
      clip.pause();
      clip.currentTime = 0;
    }
  });
}

/**
 * ### ClipAudio
 *
 * Playback state of one clip and the controls a waveform needs.
 */
export type ClipAudio = {
  /** Whether the clip is currently sounding */
  isPlaying: boolean;
  /** Playback position in `[0, 1]`; `1` once the clip has finished */
  progress: number;
  /** Peak amplitude per bucket for drawing, or `null` while loading */
  peaks: ReadonlyArray<number> | null;
  /** Clip length in seconds, `0` until known */
  duration: number;
  /** Set once the file could not be fetched, decoded, or played */
  hasError: boolean;
  /** Starts the clip from the beginning, or pauses it while playing */
  toggle: () => void;
};

type DecodedClip = {
  src: string;
  peaks: ReadonlyArray<number> | null;
  duration: number;
  hasError: boolean;
};

/**
 * ### useClipAudio
 *
 * Plays one short clip through a plain audio element and reports its
 * position every frame, alongside the peaks the waveform is drawn from.
 * Nothing starts on its own: the first sound needs a click.
 *
 * The decoded peaks are keyed by their `src`, so a clip change shows the
 * waiting state at once without resetting state inside the effect.
 *
 * @param src Clip URL
 * @returns Playback state and the toggle control
 */
export function useClipAudio(src: string): ClipAudio {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [playbackError, setPlaybackError] = useState(false);
  const [decoded, setDecoded] = useState<DecodedClip>({
    src,
    peaks: null,
    duration: 0,
    hasError: false,
  });
  const current: DecodedClip =
    decoded.src === src
      ? decoded
      : { src, peaks: null, duration: 0, hasError: false };
  const hasError = current.hasError || playbackError;

  useEffect(() => {
    let cancelled = false;

    loadPeaks(src)
      .then((result) => {
        if (cancelled) return;
        setDecoded({
          src,
          peaks: result.peaks[0] ?? [],
          duration: result.duration,
          hasError: false,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setDecoded({ src, peaks: null, duration: 0, hasError: true });
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  useEffect(() => {
    const audio = new Audio(src);
    audio.preload = 'metadata';
    audioRef.current = audio;
    liveClips.add(audio);
    let frame = 0;

    const tick = () => {
      if (audio.duration > 0) {
        setProgress(Math.min(1, audio.currentTime / audio.duration));
      }
      frame = requestAnimationFrame(tick);
    };
    const handlePlay = () => {
      setIsPlaying(true);
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(tick);
    };
    const handlePause = () => {
      setIsPlaying(false);
      cancelAnimationFrame(frame);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      cancelAnimationFrame(frame);
      setProgress(1);
      audio.currentTime = 0;
    };
    const handleError = () => {
      setIsPlaying(false);
      setPlaybackError(true);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      cancelAnimationFrame(frame);
      audio.pause();
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      liveClips.delete(audio);
      audioRef.current = null;
    };
  }, [src]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || hasError) return;

    if (audio.paused) {
      pauseOthers(audio);
      audio.currentTime = 0;
      setProgress(0);
      audio.play().catch(() => setPlaybackError(true));
      return;
    }

    audio.pause();
  }, [hasError]);

  return {
    isPlaying,
    progress,
    peaks: current.peaks,
    duration: current.duration,
    hasError,
    toggle,
  };
}
